import { Box, Button, Modal, SelectInput, TextInput, toast } from '@palmetto/palmetto-components';
import { FormikValues, useFormik } from 'formik';
import { SyntheticEvent } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import { useCreateOrganizationSiteSurveyPricingMutation } from '../../../../../../services/pricing';
import { useGetAdminUtilitiesQuery } from '../../../../../../services/utilities';

export const CreateSiteSurvey = ({
  isModalOpen,
  closeModal,
}: {
  isModalOpen: boolean;
  closeModal: (event?: SyntheticEvent) => void;
}) => {
  const [createSiteSurveyPricing] = useCreateOrganizationSiteSurveyPricingMutation();
  const { alias = '', programType = '' } = useParams<{ alias: string; programType: string }>();
  const filter = [{ id: 'programType', operation: 'Equal', selectedValues: [programType] }];
  const { data: utilities = [] } = useGetAdminUtilitiesQuery({
    advancedFilters: JSON.stringify(filter),
  });

  const formik = useFormik({
    initialValues: {
      state: [] as { label: string; value: string }[],
      amount: '' as string,
    },
    validationSchema: yup.object({
      state: yup.array().min(1, 'At least one state is required'),
      amount: yup
        .number()
        .typeError('Amount must be a number')
        .positive('Amount must be greater than 0')
        .required('Amount is required'),
    }),
    onSubmit: async (values: FormikValues, { setSubmitting, resetForm }) => {
      try {
        const states = values.state.map((s: { label: string; value: string }) => s.value);
        const amount = Number(values.amount);
        const results = await Promise.allSettled(
          states.map((state: string) =>
            createSiteSurveyPricing({
              alias,
              data: { amount, state, programType },
            }).unwrap(),
          ),
        );
        const failed = results.filter((r) => r.status === 'rejected');
        if (failed.length === 0) {
          toast.success('Site Survey pricing added successfully');
          resetForm();
          closeModal();
        } else if (failed.length === states.length) {
          toast.error('Error adding Site Survey pricing');
        } else {
          toast.error(`Added ${states.length - failed.length} of ${states.length} states — some failed`);
          resetForm();
          closeModal();
        }
      } catch (e) {
        console.error(e);
        toast.error('Error adding Site Survey pricing');
      } finally {
        setSubmitting(false);
      }
    },
  });

  const stateUtilityMapping = utilities.reduce(
    (acc, utility) => {
      acc[utility.state] = acc[utility.state] ?? [];
      acc[utility.state].push(utility);
      return acc;
    },
    {} as Record<string, any[]>,
  );

  const stateOptions = Object.keys(stateUtilityMapping)
    .toSorted()
    .map((state) => ({ label: state, value: state }));

  return (
    <Modal ariaLabelledBy="createSiteSurvey" isOpen={isModalOpen} onDismiss={closeModal} maxWidth="500px">
      <Modal.Header id="createSiteSurvey" title="Add Site Survey Pricing" onDismiss={closeModal} />
      <form onSubmit={formik.handleSubmit}>
        <Modal.Body background="secondary">
          <Box direction="column" childGap="xl">
            <SelectInput
              id="state"
              name="state"
              label="State"
              value={formik.values.state}
              onChange={(event) => formik.setFieldValue('state', event.target.value)}
              options={stateOptions}
              onBlur={formik.handleBlur}
              isDisabled={formik.isSubmitting}
              error={formik.touched.state && (formik.errors.state as string)}
              menuPortalTarget={document.body}
              isMulti
              isRequired
            />
            <TextInput
              type="number"
              label="Amount"
              name="amount"
              id="amount"
              value={formik.values.amount}
              onChange={(event) => formik.setFieldValue('amount', event.target.value)}
              onBlur={formik.handleBlur}
              prefix="$"
              error={formik.touched.amount && (formik.errors.amount as string)}
              isRequired
            />
          </Box>
        </Modal.Body>
        <Modal.Footer>
          <Button
            as="button"
            type="button"
            onClick={() => {
              formik.resetForm();
              closeModal();
            }}
            variant="secondary"
            tone="neutral"
            size="md"
            isLoading={formik.isSubmitting}
          >
            Cancel
          </Button>
          <Button variant="primary" size="md" type="submit" isLoading={formik.isSubmitting}>
            Add Site Survey Pricing
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
